import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loginSuccess } from '../../store/slices/authSlice';
import { CATEGORY_TREE } from '../../data/categoryStructure';
import { dbProducts } from '../../data/db';
import './Header.css';

const Header = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [promoImages, setPromoImages] = useState([]);
  const [promoIndex, setPromoIndex] = useState(0);
  const promoTimerRef = useRef(null);
  const navigate = useNavigate();
  const megaMenuTimeout = useRef(null);
  const userDropdownRef = useRef(null);
  const loginPopupRef = useRef(null);

  const handleNavMouseEnter = (item) => {
    clearTimeout(megaMenuTimeout.current);
    setActiveMegaMenu(item);
  };

  const handleNavMouseLeave = () => {
    megaMenuTimeout.current = setTimeout(() => setActiveMegaMenu(null), 200);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userDropdownRef.current && !userDropdownRef.current.contains(e.target) &&
        (!loginPopupRef.current || !loginPopupRef.current.contains(e.target))
      ) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When mega menu changes, compute promo images and start rotation
  useEffect(() => {
    if (!activeMegaMenu) {
      setPromoImages([]);
      setPromoIndex(0);
      if (promoTimerRef.current) {
        clearInterval(promoTimerRef.current);
        promoTimerRef.current = null;
      }
      return;
    }

    const category = CATEGORY_TREE.find((c) => c.slug === activeMegaMenu);
    if (!category) return;

    const findImagesForCategory = (category) => {
      try {
        const catSlug = (category.slug || '').toLowerCase();
        const catName = (category.name || '').toLowerCase();

        // Collect product images by matching product.category or child names
        const matches = dbProducts.products.filter((p) => {
          const prodCat = (p.category || '').toLowerCase();
          if (!prodCat) return false;
          // direct slug match
          if (prodCat.includes(catSlug.replace('-', ' '))) return true;
          // match any word from category name
          if (catName.split(/\s|&|-/).some((w) => w && prodCat.includes(w))) return true;
          // match children names
          if (category.children && category.children.some((c) => prodCat.includes((c.name || '').toLowerCase().split(' ')[0]))) return true;
          // match featuredSubcategories
          if (category.featuredSubcategories && category.featuredSubcategories.some((fs) => prodCat.includes((fs.name || '').toLowerCase().split(' ')[0]))) return true;
          return false;
        });

        let imgs = matches.map((m) => m.image).filter(Boolean);
        imgs = Array.from(new Set(imgs)).slice(0, 6);
        if (imgs.length) return imgs;

        // Try matching by child's slug words
        if (category.children) {
          const alt = dbProducts.products.filter((p) => {
            const prodCat = (p.category || '').toLowerCase();
            return category.children.some((c) => prodCat.includes((c.slug || '').split('-')[0]));
          }).map((m) => m.image).filter(Boolean);
          if (alt.length) return Array.from(new Set(alt)).slice(0, 6);
        }

        // Fallback to category asset (if exists) and finally to header logo
        const asset = `/assets/images/categories/${category.slug}.png`;
        return [asset, '/assets/images/headerlogo.png'];
      } catch (err) {
        return ['/assets/images/headerlogo.png'];
      }
    };

    const imgs = findImagesForCategory(category);
    setPromoImages(imgs);
    setPromoIndex(0);

    if (promoTimerRef.current) {
      clearInterval(promoTimerRef.current);
      promoTimerRef.current = null;
    }
    if (imgs.length > 1) {
      promoTimerRef.current = setInterval(() => {
        setPromoIndex((i) => (i + 1) % imgs.length);
      }, 3000);
    }

    return () => {
      if (promoTimerRef.current) {
        clearInterval(promoTimerRef.current);
        promoTimerRef.current = null;
      }
    };
  }, [activeMegaMenu]);


  const handleLogin = () => {
    if (mobileNumber.length < 10) return;
    dispatch(loginSuccess({
      user: {
        id: 'demo-1',
        email: `user${mobileNumber}@industrybull.com`,
        firstName: 'Demo',
        lastName: 'User',
        phone: `+91${mobileNumber}`,
        role: 'B2B',
        isVerified: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      token: 'demo-token-' + Date.now(),
    }));
    setMobileNumber('');
    setIsUserDropdownOpen(false);
  };

  return (
    <>
      {/* ── Top Header ─────────────────────────────────── */}
      <div className="top-header">
        <div className="top-header__container">
          <div className="top-header__left">
            <span className="top-header__welcome">
              Welcome to Industrybull online eCommerce store.
            </span>
          </div>
          <div className="top-header__right">
            <div className="top-header__follow">
              <span className="top-header__follow-text">Follow us:</span>
              <div className="top-header__social">
                <a href="https://facebook.com" target="_blank" rel="noreferrer" className="top-header__social-link">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noreferrer" className="top-header__social-link">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="top-header__social-link">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" className="top-header__social-link">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Middle Header ──────────────────────────────── */}
      <header className="middle-header">
        <div className="middle-header__container">
          {/* Logo */}
          <div className="middle-header__logo">
            <Link to="/">
              <img
                src="/assets/images/headerlogo.png"
                alt="Industrybull Logo"
                className="middle-header__logo-img"
              />
            </Link>
          </div>

          {/* Location + Search */}
          <div className="middle-header__center">
            <div className="middle-header__location-section">
              <span className="middle-header__location">
                <svg className="middle-header__location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Location not set
              </span>
              <button className="middle-header__location-btn">Select Delivery location</button>
            </div>
            <form 
              className="middle-header__search" 
              onSubmit={(e) => { e.preventDefault(); if(searchQuery.trim()) navigate(`/search?q=${searchQuery}`); }}
            >
              <input 
                type="text" 
                placeholder="Search Product, category Brand....." 
                className="middle-header__search-input" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="middle-header__search-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </form>
          </div>

          {/* Actions */}
          <div className="middle-header__right">

            {/* ── User / Login ──────────────────────────── */}
            <div className="mh-user" ref={userDropdownRef}>
              <button
                className="middle-header__login"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                aria-expanded={isUserDropdownOpen}
              >
                <svg className="middle-header__login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                {isAuthenticated
                  ? (user?.firstName || user?.email?.split('@')[0] || 'Account')
                  : 'Login now'}
                <svg className={`mh-chevron ${isUserDropdownOpen ? 'mh-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>

              {/* Authenticated — dropdown menu */}
              {isUserDropdownOpen && isAuthenticated && (
                <div className="acct-dropdown">
                  <Link to="/account/profile" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="8" r="5"/></svg>
                    My Profile
                  </Link>
                  <Link to="/account/orders" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="m21 16-9 5-9-5V8l9-5 9 5v8Z"/><path d="M3.3 7.8 12 12l8.7-4.2"/><path d="M12 22V12"/></svg>
                    My Orders
                  </Link>
                  <Link to="/account/rfqs" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3 2-5a8 8 0 1 1 16-2Z"/><path d="M9 9a3 3 0 1 1 5 2.2c-1 .6-1.5 1.1-1.5 2.3"/><circle cx="12" cy="17" r=".5" fill="currentColor"/></svg>
                    My RFQ's
                  </Link>
                  <Link to="/account/business" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><path d="M12 12h.01"/><path d="M2 12h20"/></svg>
                    My Business Details
                  </Link>
                  <Link to="/account/wishlist" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    My WishList
                  </Link>
                  <Link to="/account/addresses" className="acct-dropdown__item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                    My Address
                  </Link>
                </div>
              )}
            </div>

            {/* ── Login Popup (Guest) ──────────────────── */}
            {isUserDropdownOpen && !isAuthenticated && (
              <div className="login-popup-overlay" onClick={() => setIsUserDropdownOpen(false)}>
                <div className="login-popup" ref={loginPopupRef} onClick={(e) => e.stopPropagation()}>
                  {/* Close button */}
                  <button className="login-popup__close" onClick={() => setIsUserDropdownOpen(false)} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>

                  <h2 className="login-popup__title">Login <span>or</span> Sign Up</h2>
                  <p className="login-popup__subtitle">Continue to access your account</p>

                  <label className="login-popup__label">Enter Mobile Number</label>
                  <div className="login-popup__input-wrap">
                    <span className="login-popup__prefix">+91</span>
                    <input
                      type="tel"
                      className="login-popup__input"
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    />
                  </div>

                  <button className="login-popup__login-btn" onClick={handleLogin}>
                    LOGIN <span>→</span>
                  </button>

                  <div className="login-popup__or">
                    <span>or</span>
                  </div>

                  <button className="login-popup__email-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    Continue With Email ID
                  </button>

                  <button className="login-popup__google-btn">
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                    Continue With Google
                  </button>

                  <p className="login-popup__safe">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Your Detail are safe with us
                  </p>
                </div>
              </div>
            )}

            <Link to="/track/search" className="middle-header__track">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              Track Order
            </Link>

            <Link to="/cart" className="middle-header__cart">
              <span className="middle-header__cart-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg></span>
              <span className="middle-header__cart-text">Cart</span>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="middle-header__menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="middle-header__menu-line"></span>
            <span className="middle-header__menu-line"></span>
            <span className="middle-header__menu-line"></span>
          </button>
        </div>
      </header>

      {/* ── Bottom Header ──────────────────────────────── */}
      <div className="bottom-header">
        <div className="bottom-header__container">
          <ul className="bottom-header__nav">
            {CATEGORY_TREE.map((category) => (
              <li
                key={category.slug}
                className={`bottom-header__nav-item ${activeMegaMenu === category.slug ? 'bottom-header__nav-item--active' : ''}`}
                onMouseEnter={() => handleNavMouseEnter(category.slug)}
                onMouseLeave={handleNavMouseLeave}
              >
                <Link to={category.path} className="bottom-header__nav-link" onClick={() => setActiveMegaMenu(null)}>
                  {category.name}
                </Link>
                <svg className="bh-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>

                {activeMegaMenu === category.slug && (
                  <div
                    className="mega-menu"
                    onMouseEnter={() => handleNavMouseEnter(category.slug)}
                    onMouseLeave={handleNavMouseLeave}
                  >
                    <div className="mega-menu__inner">

                      <div className="mega-menu__sidebar">
                        <h4 className="mega-menu__sidebar-title">Browse {category.name}</h4>
                        <ul className="mega-menu__cat-list">
                          <li className="mega-menu__cat-item">
                            <Link to={category.path} className="mega-menu__cat-link" onClick={() => setActiveMegaMenu(null)}>
                              All {category.name}
                            </Link>
                          </li>
                          {category.children?.map((sub) => (
                            <li key={sub.slug} className="mega-menu__cat-item">
                              <Link to={sub.path} className="mega-menu__cat-link" onClick={() => setActiveMegaMenu(null)}>
                                {sub.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mega-menu__featured">
                        <div className="mega-menu__search-wrap">
                          <input
                            type="search"
                            className="mega-menu__search-input"
                            placeholder={`Search ${category.name}`}
                            aria-label={`Search ${category.name}`}
                          />
                        </div>
                        <h4 className="mega-menu__featured-title">Featured {category.name}</h4>
                        <div className="mega-menu__subcat-grid">
                          {category.featuredSubcategories?.map((sub) => (
                            <Link
                              key={sub.slug}
                              to={sub.path}
                              className="mega-menu__subcat-card"
                              onClick={() => setActiveMegaMenu(null)}
                            >
                              <span>{sub.name}</span>
                            </Link>
                          ))}
                        </div>
                        <div className="mega-menu__quick-links">
                          <h5>Quick actions</h5>
                          {category.quickLinks?.map((link) => (
                            <Link
                              key={link.label}
                              to={link.path}
                              className="mega-menu__quick-link"
                              onClick={() => setActiveMegaMenu(null)}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="mega-menu__promo">
                        <div className="mega-menu__promo-img-wrap">
                          <img
                            src={promoImages[promoIndex] || '/assets/images/headerlogo.png'}
                            alt={category.name}
                            className="mega-menu__promo-img"
                          />
                        </div>
                        <div className="mega-menu__promo-discount">Enterprise B2B</div>
                        <p className="mega-menu__promo-desc">{category.description}</p>
                        <div className="mega-menu__promo-price-row">
                          <span className="mega-menu__promo-label">Products:</span>
                          <span className="mega-menu__promo-price">{category.productCount}</span>
                        </div>
                        <Link to={category.path} className="mega-menu__promo-btn" onClick={() => setActiveMegaMenu(null)}>
                          Explore {category.name} →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Header;
